// GroupAvatar — round purple gradient `+N` chip representing a group of people.
// Mirrors GroupAvatarSm() in
// /Users/harry/Downloads/employee-portal/project/bulk-request.jsx (lines 1096–1109).
//
// Prototype values (sm, 24×24):
//   background: linear-gradient(135deg, #B5BBF9 0%, #5861DE 100%)
//                                = p.300 (azurite 300) → p.600 (azurite 600)
//   box-shadow: 0 0 0 1.5px white, 0 0 0 2.5px rgba(88,97,222,0.25)
//   font-size: 9px, font-weight: 700, color: white
//
// Note: the prototype uses the azurite (`p.*`) palette, not the secondary `p2.*`
// palette — the spec hinted at p2 but the actual gradient stops match azurite
// hex values exactly. Keeping fidelity to the prototype.

import { Box } from "@chakra-ui/react";
import { forwardRef } from "react";

export type GroupAvatarSize = "sm" | "md" | "lg";

export interface GroupAvatarProps {
  /** Group member count. Rendered as `+N`. Capped at 99 to fit. */
  count: number;
  /** Avatar size. Default `sm` (24×24). */
  size?: GroupAvatarSize;
}

// Per-size dimensions + font sizes. `sm` matches the bulk-request prototype.
// `md` / `lg` extend the scale linearly so the component composes with the
// Avatar / AppLogo sizes the rest of the system uses.
const sizeMap: Record<GroupAvatarSize, { px: number; fontSize: string }> = {
  sm: { px: 24, fontSize: "9px" },
  md: { px: 32, fontSize: "11px" },
  lg: { px: 40, fontSize: "13px" },
};

// 135° gradient from azurite-300 (#B5BBF9) → azurite-600 (#5861DE).
// Matches the prototype's inline-style values exactly.
const GRADIENT = "linear-gradient(135deg, #B5BBF9 0%, #5861DE 100%)";

// Stacked rings: thin white separator + soft azurite halo at 25%.
const RING_SHADOW =
  "0 0 0 1.5px white, 0 0 0 2.5px rgba(88, 97, 222, 0.25)";

const GroupAvatar = forwardRef<HTMLSpanElement, GroupAvatarProps>(
  ({ count, size = "sm" }, ref) => {
    const { px, fontSize } = sizeMap[size];
    return (
      <Box
        as="span"
        ref={ref}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        borderRadius="full"
        width={`${px}px`}
        height={`${px}px`}
        fontSize={fontSize}
        fontWeight={700}
        color="white"
        bg={GRADIENT}
        boxShadow={RING_SHADOW}
      >
        +{Math.min(count, 99)}
      </Box>
    );
  },
);

GroupAvatar.displayName = "GroupAvatar";

export default GroupAvatar;
